import React, { useEffect, useState } from 'react';
import { css } from 'emotion';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/core';

import { FastField, Formik } from 'formik';
import { PageIndex } from '../common/components/layouts/Page/PageIndex';
import { PrimaryButton, SecondaryButton } from '../common/components/Buttons';
import {
  Table,
  Thead,
  TH,
  TD,
  THeadRow,
  TBodyRow,
  TablePlaceholder,
} from '../common/components/Table';
import { projectsActions } from '../redux/projects/actions';
import { Pagination } from '../common/components/Pagination';
import { SelectInput } from '../common/components/Form/FormSelectInput';
import Can from '../roleBasedAccess/Can';
import { projectsAPI } from '../common/api/projects';
import { toastActions } from '../redux/toast/actions';
import { TOAST_MESSAGE } from '../common/components/Toasts/Toast';

const TYPE_OF_VISIT = {
  joint: (
    <>
      Conjointe&nbsp;
      <br />
      ergo-entreprise
    </>
  ),
  solo: (
    <>
      Ergothérapique&nbsp;
      <br />
      seule
    </>
  ),
  existing_quote: 'Devis existants',
};

const availabilityLabel = ({ positionsRemaining, expectedQuotesCount }) => {
  if (!Number.isInteger(positionsRemaining) || !Number.isInteger(expectedQuotesCount)) return null;

  const placeLabel = positionsRemaining > 1 ? 'places disponibles' : 'place disponible';
  return `${positionsRemaining} ${placeLabel} sur ${expectedQuotesCount}`;
};

const ProjectAvailability = ({ project, compact = false }) => {
  const label = availabilityLabel(project);
  if (!label) return null;

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      bg="select.bg.isSelected"
      color="coral.700"
      rounded="full"
      px={compact ? 3 : 4}
      py={1}
      fontSize={compact ? 'xs' : 'sm'}
      fontWeight="semibold"
      whiteSpace="nowrap"
    >
      {label}
    </Box>
  );
};

const ProjectKinds = ({ projectKind = [], detailed = false }) => {
  if (projectKind.length === 0) return <Text color="semiDarkGray.600">Non renseigné</Text>;

  return projectKind.map((kind, index) => (
    <Flex key={kind} alignItems="flex-start" mt={index === 0 ? 0 : 2}>
      {detailed && (
        <Text aria-hidden="true" mr={2} color="coral.700" fontWeight="bold">
          •
        </Text>
      )}
      <Text fontSize={detailed ? 'md' : 'sm'} lineHeight="1.5">
        {kind}
      </Text>
    </Flex>
  ));
};

const positioningOptions = ({ user, ignoredFromProjectCompanyIds, positionableCompanyIds }) => {
  const excludedCompanies = ignoredFromProjectCompanyIds?.map(companyId => Number(companyId)) || [];
  return (
    (user.managedCompanies || user.companies)
      ?.filter(company => positionableCompanyIds?.includes(company.id))
      .filter(company => !excludedCompanies.includes(company.id))
      .map(company => ({
        label: company.name,
        value: company.id,
      })) || []
  );
};

export const ProjectPositioningForm = ({
  id,
  user,
  ignoredFromProjectCompanyIds,
  positionableCompanyIds,
  onPosition,
  stacked = false,
  menuPortalTarget = document.body,
}) => {
  const options = positioningOptions({
    user,
    ignoredFromProjectCompanyIds,
    positionableCompanyIds,
  });
  const hasSingleOption = options.length === 1;

  return (
    <Formik
      enableReinitialize
      initialValues={{ projectId: id, companyIds: [hasSingleOption ? options[0].value : null] }}
      onSubmit={values => onPosition(values)}
    >
      {formik => (
        <Flex as="form" alignItems="stretch" flexDirection="column" onSubmit={formik.handleSubmit}>
          <FastField name="projectId" hidden />
          {!hasSingleOption && (
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              Choisir l'entreprise à positionner
            </Text>
          )}
          <Flex
            alignItems={stacked ? 'stretch' : 'center'}
            flexDirection={stacked ? 'column' : 'row'}
          >
            {!hasSingleOption && (
              <SelectInput
                accessibleColors
                aria-label="Entreprise à positionner"
                name="companyIds[0]"
                placeholder="Entreprise"
                styles={{ mb: 0, flex: 1, minW: 0 }}
                classNamePrefix="project-positioning"
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                menuPlacement="auto"
                closeMenuOnScroll={event =>
                  !event.target.closest?.('.project-positioning__menu-list')
                }
                options={options}
              />
            )}
            <PrimaryButton
              accessibleCoral
              type="submit"
              size="sm"
              ml={stacked || hasSingleOption ? 0 : 2}
              mt={stacked ? 3 : 0}
              width={stacked || hasSingleOption ? '100%' : 'auto'}
              disabled={!formik.values.companyIds[0]}
            >
              {hasSingleOption ? 'Se positionner' : 'Positionner'}
            </PrimaryButton>
          </Flex>
        </Flex>
      )}
    </Formik>
  );
};

const ProjectCard = ({ project, user, onPosition, onOpenDetails }) => {
  const {
    id,
    formattedId,
    city,
    department,
    projectKind,
    projectPublicationDate,
    typeOfVisit,
    ignoredFromProjectCompanyIds,
    positionableCompanyIds,
  } = project;

  return (
    <Box
      data-testid="project-place-card"
      bg="white"
      border="1px solid"
      borderColor="lightGray3"
      borderTop="4px solid"
      borderTopColor="coral.900"
      rounded="lg"
      boxShadow="0 4px 12px rgba(46, 73, 112, 0.1)"
      p={4}
      mb={4}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" gridGap={3}>
        <Box>
          <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase">
            N° de dossier
          </Text>
          <Text fontFamily="heading" fontSize="xl" fontWeight="bold" color="semiDarkGray.900">
            {formattedId}
          </Text>
        </Box>
        <Flex direction="column" alignItems="flex-end" gridGap={2}>
          <Box
            bg="select.bg.isSelected"
            color="coral.700"
            rounded="full"
            px={3}
            py={1}
            fontSize="sm"
            fontWeight="semibold"
          >
            Dépt. {department}
          </Box>
          <ProjectAvailability project={project} compact />
        </Flex>
      </Flex>

      <Text mt={3} fontWeight="semibold" color="semiDarkGray.900" wordBreak="break-word">
        {city}
      </Text>
      <Box mt={3} pt={3} borderTop="1px solid" borderColor="lightGray3">
        <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase" mb={1}>
          Qualification du marché
        </Text>
        <ProjectKinds projectKind={projectKind} />
      </Box>
      <Flex mt={3} gridGap={5} alignItems="flex-start">
        <Box flex="1">
          <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase">
            Date de mise à disposition
          </Text>
          <Text mt={1} fontSize="sm" fontWeight="semibold">
            {projectPublicationDate}
          </Text>
        </Box>
        <Box flex="1">
          <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase">
            Modalité de visite
          </Text>
          <Text mt={1} fontSize="sm" fontWeight="semibold">
            {TYPE_OF_VISIT[typeOfVisit] || '—'}
          </Text>
        </Box>
      </Flex>
      <Box mt={4} pt={4} borderTop="1px solid" borderColor="lightGray3">
        <SecondaryButton
          accessibleCoral
          type="button"
          size="sm"
          width="100%"
          onClick={() => onOpenDetails(project)}
        >
          Voir le détail
        </SecondaryButton>
        <Can
          perform="project-place:position"
          yes={() => (
            <Box mt={3}>
              <ProjectPositioningForm
                id={id}
                user={user}
                ignoredFromProjectCompanyIds={ignoredFromProjectCompanyIds}
                positionableCompanyIds={positionableCompanyIds}
                onPosition={onPosition}
                stacked
              />
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

const ConnectedProjectsIndex = ({ projects, getProjects, pending, meta }) => {
  const user = useSelector(state => state.authentication.profile);
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPosition, setCurrentPosition] = useState({});
  const [positioningContext, setPositioningContext] = useState({});
  const [detailProject, setDetailProject] = useState(null);
  const [success, setSuccess] = useState(false);
  const [onSubmitPending, setOnSubmitPending] = useState(false);

  useEffect(() => {
    getProjects(currentPage);
  }, [getProjects, currentPage]);

  const onModalClose = () => {
    if (success) getProjects(currentPage);
    setSuccess(false);
    setCurrentPosition({});
    setPositioningContext({});
    onClose();
  };

  const openPositionConfirmation = (project, values) => {
    const companyId = values.companyIds[0]?.value ?? values.companyIds[0];
    const companyName = (user.managedCompanies || user.companies)?.find(
      company => company.id === companyId,
    )?.name;

    setCurrentPosition(values);
    setPositioningContext({ project, companyName });
    onOpen();
  };

  const onSubmit = () => {
    setOnSubmitPending(true);
    const { projectId, companyIds } = currentPosition;

    const finalData = {
      companyIds: companyIds.map(company => company?.value ?? company),
    };

    projectsAPI
      .updateProject(projectId, finalData)
      .then(() => setSuccess(true))
      .catch(() => {
        onModalClose();
        dispatch(toastActions.error(TOAST_MESSAGE.error.projectPosition));
      })
      .finally(() => setOnSubmitPending(false));
  };

  const renderMobileProjects = () => {
    if (!projects || pending) {
      return (
        <Flex minH="12rem" alignItems="center" justifyContent="center" color="gray">
          Chargement...
        </Flex>
      );
    }

    if (projects.length === 0) {
      return (
        <Box textAlign="center" py={8}>
          <Text>Aucune proposition concernant vos secteurs n'est actuellement disponible.</Text>
          {meta?.lastPositionedAt && (
            <Text mt={2} color="semiDarkGray.600" fontSize="sm">
              La dernière proposition disponible a été prise le{' '}
              {new Date(meta.lastPositionedAt)
                .toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
                .replace(' ', ' à ')
                .replace(':', 'h')}
            </Text>
          )}
        </Box>
      );
    }

    return projects.map(project => (
      <ProjectCard
        key={project.id}
        project={project}
        user={user}
        onPosition={values => openPositionConfirmation(project, values)}
        onOpenDetails={setDetailProject}
      />
    ));
  };

  return (
    <PageIndex responsive>
      <Box
        className={css({ display: 'none', '@media (min-width: 100em)': { display: 'block' } })}
        overflowX="auto"
        w="100%"
        pb={2}
        data-testid="projects-table-scroll"
      >
        <Table minWidth="1120px">
          <Thead>
            <THeadRow>
              <TH width="0" minWidth="100px" size="sm">
                N° de dossier
              </TH>
              <TH width="0" minWidth="10vw" wordBreak="break-word">
                Ville
              </TH>
              <TH>Dépt.</TH>
              <TH width="100%">Qualification du marché</TH>
              <TH width="0" minWidth="150px">
                Date de mise
                <br /> à disposition
              </TH>
              <TH width="0" minWidth="170px">
                Modalité de visite
              </TH>
              <TH width="0" minWidth="210px">
                Places disponibles
              </TH>
              <TH width="0" minWidth="300px">
                Détails et actions
              </TH>
            </THeadRow>
          </Thead>
          <tbody>
            {/* eslint-disable-next-line no-nested-ternary */}
            {!projects || pending ? (
              <TablePlaceholder colSpan={8} />
            ) : projects.length === 0 ? (
              <tr>
                <TD colSpan={8} textAlign="center" py={4}>
                  <Text>
                    Aucune proposition concernant vos secteurs n'est actuellement disponible.
                  </Text>
                  {meta?.lastPositionedAt && (
                    <Text mt={2}>
                      La dernière proposition disponible a été prise le{' '}
                      {new Date(meta.lastPositionedAt)
                        .toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                        .replace(' ', ' à ')
                        .replace(':', 'h')}
                    </Text>
                  )}
                </TD>
              </tr>
            ) : (
              projects?.map(project => {
                const {
                  id,
                  formattedId,
                  city,
                  department,
                  projectKind,
                  projectPublicationDate,
                  typeOfVisit,
                  ignoredFromProjectCompanyIds,
                  positionableCompanyIds,
                } = project;

                return (
                  <TBodyRow key={id} enableHover={false}>
                    <TD hasPadding>{formattedId}</TD>
                    <TD hasPadding>{city}</TD>
                    <TD hasPadding>{department}</TD>
                    <TD hasPadding whiteSpace="pre-line" lineHeight="1.5">
                      <ProjectKinds projectKind={projectKind} />
                    </TD>
                    <TD hasPadding>{projectPublicationDate}</TD>
                    <TD hasPadding>{TYPE_OF_VISIT[typeOfVisit] || ''}</TD>
                    <TD hasPadding>
                      <ProjectAvailability project={project} />
                    </TD>
                    <TD hasPadding>
                      <SecondaryButton
                        accessibleCoral
                        type="button"
                        size="sm"
                        width="100%"
                        mb={3}
                        onClick={() => setDetailProject(project)}
                      >
                        Voir le détail
                      </SecondaryButton>
                      <Can
                        perform="project-place:position"
                        yes={() => (
                          <ProjectPositioningForm
                            id={id}
                            user={user}
                            ignoredFromProjectCompanyIds={ignoredFromProjectCompanyIds}
                            positionableCompanyIds={positionableCompanyIds}
                            onPosition={values => openPositionConfirmation(project, values)}
                          />
                        )}
                      />
                    </TD>
                  </TBodyRow>
                );
              })
            )}
          </tbody>
        </Table>
      </Box>
      <Box
        className={css({ display: 'grid', '@media (min-width: 100em)': { display: 'none' } })}
        gridTemplateColumns={{ base: 'minmax(0, 1fr)', xl: 'repeat(2, minmax(0, 1fr))' }}
        gridColumnGap={4}
        pt={4}
        data-testid="project-place-mobile-list"
      >
        {renderMobileProjects()}
      </Box>
      <ConfirmationModal
        isOpen={isOpen}
        onSubmit={onSubmit}
        pending={onSubmitPending}
        success={success}
        onClose={onModalClose}
        context={positioningContext}
      />
      <ProjectDetailsModal
        key={detailProject?.id || 'closed'}
        project={detailProject}
        user={user}
        onClose={() => setDetailProject(null)}
        onPosition={(project, values) => {
          setDetailProject(null);
          openPositionConfirmation(project, values);
        }}
      />
      {meta && (
        <Pagination
          current={currentPage}
          total={meta.total}
          defaultPageSize={meta.perPage}
          onChange={page => setCurrentPage(page)}
        />
      )}
    </PageIndex>
  );
};

const mapStateToProps = ({ projects: state }) => ({
  pending: state?.pending,
  projects: state?.data,
  meta: state?.meta,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getProjects: projectsActions.getProjects,
    },
    dispatch,
  );

export const ProjectsTab = connect(mapStateToProps, mapDispatchToProps)(ConnectedProjectsIndex);

const ProjectDetailsModal = ({ project, user, onClose, onPosition }) => {
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);
  const [details, setDetails] = useState(null);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const projectId = project?.id;

  useEffect(() => {
    if (!projectId) return undefined;

    let active = true;
    setPending(true);
    setDetails(null);
    setError(null);
    projectsAPI
      .getProject(projectId)
      .then(data => {
        if (active) setDetails(data.project);
      })
      .catch(failure => {
        if (active) {
          setError(
            failure.response?.status === 404
              ? 'Ce projet n’est plus disponible dans votre Project Place.'
              : 'Le détail du projet n’a pas pu être chargé.',
          );
        }
      })
      .finally(() => {
        if (active) setPending(false);
      });

    return () => {
      active = false;
    };
  }, [projectId, retry]);

  if (!project) return null;

  const currentProject = details || project;

  const {
    id,
    formattedId,
    city,
    department,
    projectKind,
    projectPublicationDate,
    typeOfVisit,
    ignoredFromProjectCompanyIds,
    positionableCompanyIds,
  } = currentProject;

  const recommendationStatus = {
    validated: 'Validées',
    pending_validation: 'En attente de validation',
    not_available: 'Pas encore disponibles',
  }[details?.recommendationsStatus];

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent
        ref={setMenuPortalTarget}
        rounded="lg"
        maxHeight="calc(100vh - 2rem)"
        overflowY="auto"
      >
        <ModalHeader>Détails du projet</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap="wrap"
            gridGap={3}
            mb={5}
          >
            <Box minW={0} maxW="100%">
              <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase">
                Dossier
              </Text>
              <Text fontFamily="heading" fontSize="2xl" fontWeight="bold">
                N° {formattedId}
              </Text>
              <Text mt={1} color="semiDarkGray.600" wordBreak="break-word">
                {city} · Département {department}
              </Text>
            </Box>
            <ProjectAvailability project={currentProject} />
          </Flex>

          <Flex flexWrap="wrap" gridGap={4} mb={5}>
            <Box flex="1" minWidth="180px" p={3} bg="lightGray1" rounded="md">
              <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase">
                Mise à disposition
              </Text>
              <Text mt={1} fontWeight="semibold">
                {projectPublicationDate || 'Non renseignée'}
              </Text>
            </Box>
            <Box flex="1" minWidth="180px" p={3} bg="lightGray1" rounded="md">
              <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase">
                Modalité de visite
              </Text>
              <Text mt={1} fontWeight="semibold">
                {TYPE_OF_VISIT[typeOfVisit] || 'Non renseignée'}
              </Text>
            </Box>
          </Flex>

          {pending && (
            <Box role="status" py={8} textAlign="center" color="semiDarkGray.600">
              Chargement du détail…
            </Box>
          )}

          {error && (
            <Box role="alert" py={5}>
              <Text mb={3}>{error}</Text>
              <SecondaryButton accessibleCoral onClick={() => setRetry(value => value + 1)}>
                Réessayer
              </SecondaryButton>
            </Box>
          )}

          {details && (
            <>
              <Box borderTop="1px solid" borderColor="lightGray3" py={5}>
                <Text fontFamily="heading" fontSize="lg" fontWeight="bold" mb={3}>
                  Logement
                </Text>
                <Flex gridGap={5} flexWrap="wrap">
                  <Box flex="1" minW="130px">
                    <Text fontSize="sm" color="semiDarkGray.600">
                      Type de logement
                    </Text>
                    <Text mt={1} fontWeight="semibold">
                      {{ maison: 'Maison', appartement: 'Appartement' }[details.housingCategory] ||
                        'Non renseigné'}
                    </Text>
                  </Box>
                  <Box flex="1" minW="130px">
                    <Text fontSize="sm" color="semiDarkGray.600">
                      Typologie
                    </Text>
                    <Text mt={1} fontWeight="semibold">
                      {details.housingType || 'Non renseignée'}
                    </Text>
                  </Box>
                </Flex>
              </Box>
              <Box borderTop="1px solid" borderColor="lightGray3" pt={5}>
                <Text fontFamily="heading" fontSize="lg" fontWeight="bold">
                  Préconisations techniques
                </Text>
                <Text
                  mt={1}
                  mb={4}
                  fontSize="sm"
                  fontWeight="semibold"
                  color={
                    details.recommendationsStatus === 'validated' ? 'green.700' : 'semiDarkGray.600'
                  }
                >
                  {recommendationStatus}
                </Text>
                {details.recommendationsStatus === 'validated' ? (
                  <>
                    <Text fontWeight="semibold" mb={2}>
                      Pièces concernées
                    </Text>
                    <Text mb={4}>{details.affectedRooms?.join(', ') || 'Non renseignées'}</Text>
                    <Text fontWeight="semibold" mb={2}>
                      Travaux préconisés
                    </Text>
                    <ProjectKinds projectKind={details.works} detailed />
                    <Flex mt={4} justifyContent="space-between" flexWrap="wrap" gridGap={2}>
                      <Text fontWeight="semibold">Fiches techniques annexes</Text>
                      <Text>
                        {details.technicalSheetAvailable === null ||
                        details.technicalSheetAvailable === undefined
                          ? 'Non renseigné'
                          : { true: 'Présentes', false: 'Non signalées' }[
                              details.technicalSheetAvailable
                            ]}
                      </Text>
                    </Flex>
                  </>
                ) : (
                  <>
                    <Text fontWeight="semibold" mb={2}>
                      Projet envisagé
                    </Text>
                    <ProjectKinds projectKind={projectKind} detailed />
                  </>
                )}
              </Box>
            </>
          )}

          <Box mt={5} p={4} bg="select.bg.isSelected" rounded="md">
            <Text fontSize="sm" color="semiDarkGray.900">
              Les coordonnées du bénéficiaire et les informations nécessaires à la préparation du
              devis seront transmises après le positionnement.
            </Text>
          </Box>

          <Can
            perform="project-place:position"
            yes={() =>
              details && (
                <Box mt={5} pt={5} borderTop="1px solid" borderColor="lightGray3">
                  <Text fontWeight="semibold" mb={3}>
                    Se positionner sur ce projet
                  </Text>
                  <ProjectPositioningForm
                    id={id}
                    user={user}
                    ignoredFromProjectCompanyIds={ignoredFromProjectCompanyIds}
                    positionableCompanyIds={positionableCompanyIds}
                    onPosition={values => onPosition(currentProject, values)}
                    menuPortalTarget={menuPortalTarget}
                    stacked
                  />
                </Box>
              )
            }
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const ConfirmationModal = ({ isOpen, onSubmit, pending, success, onClose, context }) => {
  const { project, companyName } = context;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent rounded="lg">
        <ModalHeader>Confirmation du positionnement</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {project && (
            <Box mb={4} p={4} bg="lightGray1" rounded="md">
              <Text fontSize="xs" color="semiDarkGray.600" textTransform="uppercase">
                Positionnement demandé
              </Text>
              <Text mt={1} fontWeight="bold" wordBreak="break-word">
                {companyName || 'Votre entreprise'} · Dossier n° {project.formattedId}
              </Text>
              <Text mt={1} fontSize="sm" color="semiDarkGray.600" wordBreak="break-word">
                {project.city} · Département {project.department}
              </Text>
              <Box mt={3}>
                <ProjectAvailability project={project} compact />
              </Box>
            </Box>
          )}
          {success ? (
            <p>
              Votre positionnement a bien été enregistré. Vous recevrez prochainement les
              informations relatives au projet pour établir votre devis.
            </p>
          ) : (
            <p>Êtes-vous certain de vouloir vous positionner sur ce marché ?</p>
          )}
        </ModalBody>

        <ModalFooter>
          {success ? (
            <PrimaryButton accessibleCoral onClick={onClose}>
              OK
            </PrimaryButton>
          ) : (
            <>
              <SecondaryButton accessibleCoral mr={3} onClick={onClose}>
                Non
              </SecondaryButton>
              <PrimaryButton
                accessibleCoral
                onClick={onSubmit}
                isLoading={pending}
                loadingText="Positionnement en cours"
              >
                Oui
              </PrimaryButton>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
